import { useEffect, useRef, useState } from 'react'
import { Search, X } from 'lucide-react'
import { cn } from '~/lib/utils'

// 재사용 검색 입력 — 디바운스(300ms) 후에만 onChange를 호출한다.
// 좌측 Search 아이콘, 값이 있으면 우측 X 클리어 버튼. 다크 콘솔 톤(h-8·각진·bg-card·border-border·포커스 링).
// 제어형(value) + 내부 로컬 상태로 입력 즉시 반영, 외부 value 변경(초기화 등)은 동기화한다.
export interface SearchInputProps {
    value: string
    onChange: (value: string) => void
    placeholder?: string
    className?: string
}

const DEBOUNCE_MS = 300

export function SearchInput({
    value,
    onChange,
    placeholder = '검색',
    className,
}: SearchInputProps) {
    // 입력 즉시 반영용 로컬 상태. 외부 value가 바뀌면(예: 검색 초기화) 맞춘다.
    const [local, setLocal] = useState(value)

    // onChange는 호출 측에서 인라인으로 새로 만들어지는 경우가 많아 ref로 안정화한다(디바운스 재예약 방지).
    const onChangeRef = useRef(onChange)
    onChangeRef.current = onChange

    useEffect(() => {
        setLocal(value)
    }, [value])

    // 로컬 입력이 멎고 300ms 뒤에만 onChange 호출. 외부 value와 같으면(동기화 직후 등) 호출하지 않는다.
    useEffect(() => {
        if (local === value) return
        const t = setTimeout(() => onChangeRef.current(local), DEBOUNCE_MS)
        return () => clearTimeout(t)
    }, [local, value])

    const clear = () => {
        setLocal('')
        onChangeRef.current('')
    }

    return (
        <div className={cn('relative', className)}>
            <Search
                className='text-muted-foreground pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2'
                aria-hidden
            />
            <input
                type='search'
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                placeholder={placeholder}
                aria-label={placeholder}
                className='border-border bg-card text-foreground placeholder:text-muted-foreground focus:border-primary focus:ring-ring/20 h-8 w-full rounded-[var(--radius)] border pr-8 pl-8 text-[13px] transition-colors focus:ring-2 focus:outline-none [&::-webkit-search-cancel-button]:appearance-none'
            />
            {local ? (
                <button
                    type='button'
                    aria-label='검색어 지우기'
                    onClick={clear}
                    className='text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 transition-colors'>
                    <X className='size-4' />
                </button>
            ) : null}
        </div>
    )
}
